import { Fragment, useRef } from "react";
import {
  StyledCell,
  StyledCellContainer,
  StyledCellContentCollaborators,
  StyledCellContentDownload,
  StyledMatrixButton,
  StyledQuadrantAxisLabels,
  StyledMatrix,
} from "../MatrixGrid/MatrixGrid.styled";
import {
  CollaboratorInfo,
  FontWeightEnum,
  IconSizeEnum,
} from "../../types/AppTypes";
import { BodyTyp, CaptionsTyp, TitlesTyp } from "../Typography/Typography";
import { IconManager } from "../core/IconManager/IconManager";
import { MatrixPerformanceItem } from "../../types/api/potentialMatrix.type";
import { theme } from "../../constants/StyledComponents";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { useAppSelector } from "../../hooks/store.hook";
import { EMPTY_STRING } from "../../constants/Dictionary";
import { Loader } from "../core/Loader/Loader";
import {
  COLLABORATORS_LIMIT,
  IMPACT_RANGES,
  IMPACT_ROW_COLORS,
  internalGradeColors,
  MatrixSizes,
  MatrixViewModeV3,
} from "../../constants/PotentialMatrixConstants";
import { ReactComponent as DetailsIcon } from "../../icons/details_search.svg";
import { Chip, ChipSizeEnum } from "../core/Chip/Chip";
import { CollaboratorChip } from "../core/CollaboratorChip/CollaboratorChip";
import { DownloadCollaboratorChip } from "../core/DownloadCollaboratorChip/DownloadCollaboratorChip";
import { Divider } from "../core/Divider/Divider";
import { NumberChip } from "../core/NumberChip/NumberChip";
import { BadgeChip, BadgeVariant } from "../BadgeChip/BadgeChip";

type MatrixPerformanceGridProps = {
  data: MatrixPerformanceItem[];
  size: MatrixSizes;
  yearSelected: number;
  loading?: boolean;
  viewMode?: MatrixViewModeV3;
  onClickResume?: (collaborator: CollaboratorInfo) => void;
  selectedItem?: MatrixPerformanceItem;
  onClickItem?: (item: MatrixPerformanceItem) => void;
  showDownloadPreview?: boolean;
  isAnimatingOpen?: boolean;
};

export const MatrixPerformanceGrid = ({
  data,
  selectedItem,
  size,
  yearSelected,
  loading = false,
  viewMode,
  onClickItem,
  onClickResume,
  showDownloadPreview = false,
  isAnimatingOpen,
}: MatrixPerformanceGridProps) => {
  const { config } = useAppSelector((state) => state.config);
  const divRef = useRef<HTMLDivElement>(null);

  const potentialMatrixConfig = config.find(
    (item) => item.year === yearSelected,
  )?.config?.finalInterviewDimensions;

  if (!potentialMatrixConfig) {
    return null;
  }

  const orderedImpactRanges = [...IMPACT_RANGES].reverse();

  const rowValues = orderedImpactRanges.map((r) => ({
    label: r.label,
    color: IMPACT_ROW_COLORS[r.label],
  }));

  const calcMatrix = () => {
    const matrix = [] as [string, number, string, number[]][][];
    rowValues.forEach((rv) => {
      const row = [] as [string, number, string, number[]][];
      potentialMatrixConfig.forEach((c) => {
        const cell: [string, number, string, number[]] = [
          rv.label,
          c.id,
          rv.color,
          c.range,
        ];
        row.push(cell);
      });
      matrix.push(row);
    });
    return matrix;
  };

  const matrix = calcMatrix();

  const renderCollaboratorsCellContent = (
    matrixItem: MatrixPerformanceItem,
  ) => {
    const COLLABORATOR_SLOTS = COLLABORATORS_LIMIT.PERFORMANCE;
    const COLLABORATOR_INTERNAL_GRADE_SLOTS = 6;
    const numberOfCollaboratorsToShowed = matrixItem.collaborators.length;
    const paddedCollaborators = Array.from(
      { length: COLLABORATOR_SLOTS },
      (_, i) => matrixItem.collaborators[i] ?? null,
    );

    const hiddenCollaboratorsCount =
      matrixItem.count - numberOfCollaboratorsToShowed;

    return (
      <StyledCellContentCollaborators>
        <div className="cell-content-collaborators-header">
          <div className="cell-content-collaborators-container">
            {paddedCollaborators.map((item, index) => (
              <div
                key={item?.id ?? `placeholder-${index}`}
                className={`collaborator-chip-slot ${item == null ? "invisible" : ""}`}
              >
                {item != null ? (
                  <CollaboratorChip
                    collaborator={item}
                    {...(item.internalGrade && {
                      borderColor: internalGradeColors[item.internalGrade],
                    })}
                    onClick={onClickResume}
                    size={ChipSizeEnum.SMALL}
                  />
                ) : (
                  <div style={{ height: 18 }} />
                )}
              </div>
            ))}
          </div>
          {hiddenCollaboratorsCount > 0 && (
            <Chip
              option={{
                value: EMPTY_STRING,
                label: (
                  <StyledMatrixButton>
                    <div className="details-icon-wrapper">
                      <IconManager
                        size={IconSizeEnum.SMALL}
                        color={theme.colors.black}
                      >
                        <DetailsIcon />
                      </IconManager>
                    </div>
                    <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                      {`Totale ${hiddenCollaboratorsCount}`}
                    </BodyTyp>
                  </StyledMatrixButton>
                ),
              }}
              size={ChipSizeEnum.SMALL}
              fullWidth
              hoverEffect
              {...(matrixItem &&
                onClickItem && {
                  onSelectChip: () => onClickItem(matrixItem),
                })}
              selectedChip
            />
          )}
        </div>
        <Divider margin={6} vertical color={theme.colors.purple} />
        <div className="number-chip-box">
          {Array.from({ length: COLLABORATOR_INTERNAL_GRADE_SLOTS }, (_, i) => {
            const internalGrade = matrixItem.internalGrades[i] ?? null;
            return (
              <div
                key={internalGrade?.name ?? `ig-placeholder-${i}`}
                className={`number-chip-container ${internalGrade == null || internalGrade.count === 0 ? "invisible" : ""}`}
              >
                {internalGrade != null ? (
                  <NumberChip
                    id={internalGrade.name}
                    color={internalGradeColors[internalGrade.name]}
                    number={internalGrade.count ?? 0}
                  />
                ) : (
                  <div style={{ height: 18 }} />
                )}
              </div>
            );
          })}
        </div>
      </StyledCellContentCollaborators>
    );
  };

  const renderDownloadCellContent = (matrixItem?: MatrixPerformanceItem) => {
    return (
      <StyledCellContentDownload>
        {matrixItem && (
          <div className="cell-content-collaborators-header">
            <div className="cell-content-collaborators-container">
              {matrixItem.collaborators.map((item) => (
                <DownloadCollaboratorChip
                  key={item.id}
                  collaborator={item}
                  {...(item.internalGrade && {
                    borderColor: internalGradeColors[item.internalGrade],
                  })}
                />
              ))}
            </div>
          </div>
        )}
      </StyledCellContentDownload>
    );
  };

  const renderCellContent = (
    item: MatrixPerformanceItem,
    row: string,
    col: number,
    count: number,
  ) => {
    if (!isBig) {
      if (
        selectedItem &&
        selectedItem.impact === row &&
        selectedItem.dimensionId === col
      ) {
        return (
          <div className="count-container">
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.black}>
              <CheckIcon />
            </IconManager>
          </div>
        );
      }
      return null;
    }

    if (showDownloadPreview) {
      return renderDownloadCellContent(item);
    }

    if (viewMode === MatrixViewModeV3.COLLABORATOR) {
      if (count === 0) {
        return null;
      }

      return renderCollaboratorsCellContent(item);
    }

    return null;
  };

  const renderEmptyCellContent = () => {
    if (showDownloadPreview) {
      return renderDownloadCellContent();
    }

    return null;
  };

  const getItemData = (row: string, col: number) => {
    return data.find(
      (d) => d.dimensionId === col && d.impact === row && d.count > 0,
    );
  };

  const isBig = size === MatrixSizes.BIG;

  return (
    <StyledMatrix $size={size} $isAnimatingOpen={isAnimatingOpen}>
      <div className="matrix-content">
        <div className="row-title">
          {orderedImpactRanges.map((l) =>
            isBig ? (
              <div className="container-internal" key={l.label}>
                <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="1">
                  {l.label &&
                    l.label.charAt(0).toUpperCase() +
                      l.label.slice(1).toLowerCase()}
                </BodyTyp>
                <BadgeChip colorVariant={BadgeVariant.PRIMARY}>
                  {l.min} - {l.max}
                </BadgeChip>
              </div>
            ) : (
              <CaptionsTyp key={l.label} fontWeight={FontWeightEnum.SEMIBOLD}>
                {l.label &&
                  l.label.charAt(0).toUpperCase() +
                    l.label.slice(1).toLowerCase()}
              </CaptionsTyp>
            ),
          )}
        </div>
        <Loader
          loading={loading}
          size={20}
          className="loader-matrix-container"
          contentClassName="loader-matrix-content"
        >
          <div className="matrix-grid" ref={divRef}>
            {matrix.map((row) =>
              row.map((cell, index) => {
                const [row, col, color] = cell;
                const item = getItemData(row as string, col as number);

                const isNotGlobal =
                  item &&
                  item.count > 0 &&
                  viewMode !== MatrixViewModeV3.GLOBAL;

                return (
                  <StyledCell
                    key={`${cell}${index}`}
                    $bgColor={color as string}
                    $size={size}
                    $showDownloadPreview={showDownloadPreview}
                  >
                    <StyledCellContainer
                      {...(item &&
                        viewMode === MatrixViewModeV3.GLOBAL &&
                        onClickItem && {
                          $clickable: true,
                        })}
                    >
                      <div
                        className={
                          isBig
                            ? `cell-main-box ${isNotGlobal ? "active" : ""}`
                            : "cell-main-box-small"
                        }
                        {...(item &&
                          viewMode === MatrixViewModeV3.GLOBAL &&
                          onClickItem && {
                            onClick: () => onClickItem(item),
                          })}
                      >
                        {data.map((d) => {
                          const [row, col] = cell;
                          if (d.dimensionId === col && d.impact === row) {
                            if (item) {
                              return (
                                <Fragment key={`${d.impact}-${d.dimensionId}`}>
                                  {renderCellContent(
                                    item,
                                    row as string,
                                    col as number,
                                    d.count,
                                  )}
                                </Fragment>
                              );
                            } else {
                              return (
                                <Fragment key={`${d.impact}-${d.dimensionId}`}>
                                  {renderEmptyCellContent()}
                                </Fragment>
                              );
                            }
                          }
                          return null;
                        })}
                      </div>
                    </StyledCellContainer>
                  </StyledCell>
                );
              }),
            )}
          </div>
        </Loader>
      </div>
      <StyledQuadrantAxisLabels
        $size={size}
        $numCol={potentialMatrixConfig.length}
      >
        {potentialMatrixConfig.map((v) => (
          <div className="axis-cell" key={v.initials}>
            {isBig ? (
              <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                {v.range.length === 2
                  ? `${v.initials} | ${v.range[0]}-${v.range[1]}%`
                  : `${v.initials} | ${EMPTY_STRING}`}
              </TitlesTyp>
            ) : (
              <>
                <CaptionsTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                  {v.initials}
                </CaptionsTyp>
                <CaptionsTyp fontWeight={FontWeightEnum.SEMIBOLD}>
                  {v.range.length === 2
                    ? `${v.range[0]}-${v.range[1]}%`
                    : EMPTY_STRING}
                </CaptionsTyp>
              </>
            )}
          </div>
        ))}
      </StyledQuadrantAxisLabels>
    </StyledMatrix>
  );
};
